import React, {MutableRefObject, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import jsPDF from 'jspdf';
import {useNavigate} from 'react-router-dom';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import Button from '../../../../incase-domain/components/button/Button';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import {httpGetWithHeaders} from '../../../../service/http.service';
import ResetReports from './ResetReports';
import {nodeToBase64} from '../../../../utils/nodeToImage/nodeToBase64';

interface ReportsHeaderProps {
    onClickRefresh?: () => void;
    reportsRef: MutableRefObject<HTMLDivElement | null>;
    filterQuery: Record<string, string | string[] | undefined>;
}

function ReportsHeader({onClickRefresh, reportsRef, filterQuery}: ReportsHeaderProps): JSX.Element | null {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const selectedPresentation = useSelector(selectedPresentationSelector);

    const from = typeof filterQuery.from === 'string' ? filterQuery.from : undefined;
    const to = typeof filterQuery.to === 'string' ? filterQuery.to : undefined;

    const [isLoading, setIsLoading] = useState(false);

    const handleDownloadReport = async (endpoint: string, defaultFilename: string): Promise<void> => {
        setIsLoading(true);
        try {
            const query = new URLSearchParams();
            if (from) query.append('from', from);
            if (to) query.append('to', to);
            const url = `admin/analytics/${endpoint}/${selectedPresentation._id}${query.toString() ? `?${query.toString()}` : ''}`;

            const response = await httpGetWithHeaders(url, {responseType: 'blob'});
            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const filename = response.headers['x-filename'] || defaultFilename;

            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } finally {
            setIsLoading(false);
        }
    };

    function handleReportToPdf(): void {
        setIsLoading(true);
        if (!reportsRef.current) {
            setIsLoading(false);
            return;
        }

        setTimeout(() => {
            if (!reportsRef.current) return;

            const img = new Image();
            img.onload = (): void => {
                const {width: imgWidth, height: imgHeight} = img;

                const width = 800;
                const height = (imgHeight * width) / imgWidth;

                img.width = width;
                img.height = height;

                // eslint-disable-next-line new-cap
                const doc = new jsPDF({
                    orientation: 'p',
                    unit: 'pt',
                    format: [width, height],
                });

                doc.addImage(img, 'JPEG', 0, 0, width, height);

                doc.save(`report-${selectedPresentation.name}.pdf`);
                setIsLoading(false);
            };

            nodeToBase64(reportsRef.current, () => true, 1).then((base64) => {
                img.src = base64;
            });
        }, 0);
    }

    return (
        <div className="c-reportFilter">
            <div className="c-pageHeader u-h-between">
                <Button
                    variant="ghost"
                    color="frost"
                    leading="solar:alt-arrow-left-linear"
                    square
                    onClick={() => {
                        navigate(`/presentations/${selectedPresentation._id}`);
                    }}
                />
                <div className="u-flex u-v-center u-gap-3">
                    <div className="c-pageHeader__title">
                        <div>{t('reportPage.title')}</div>
                    </div>
                    &bull;
                    <div className="c-reportFilter__header_presentationName">{selectedPresentation.name}</div>
                </div>
                <div style={{flex: 1}} />
                <div className="u-flex u-gap-1">
                    <Button leading="solar:refresh-linear" square variant="ghost" size="sm" color="primary" onClick={onClickRefresh}>
                        {t('navigation.refresh')}
                    </Button>
                    <DropdownMenuCompact
                        menuPosition={DropdownMenuCompactMenuPosition.LEFT}
                        items={[
                            [
                                {
                                    label: t('reportsPage.excel'),
                                    onClick: () => handleDownloadReport('exportExcel', 'report.xlsx'),
                                },
                                {
                                    label: t('reportsPage.pdf'),
                                    onClick: () => {
                                        handleReportToPdf();
                                    },
                                },
                                {
                                    label: t('reportsPage.csv'),
                                    onClick: () => handleDownloadReport('exportCSV', 'report.csv'),
                                },
                                {
                                    label: t('reportsPage.pptx'),
                                    onClick: () => handleDownloadReport('exportPptx', 'report.pptx'),
                                },
                            ],
                        ]}
                    >
                        <Button variant="ghost" color="primary" size="sm" leading="solar:download-minimalistic-linear" loading={isLoading}>
                            {t('editorPage.download')}
                        </Button>
                    </DropdownMenuCompact>
                    <ResetReports selectedPresentationId={selectedPresentation._id} />
                </div>
            </div>
        </div>
    );
}

export default ReportsHeader;
